import dayjs from 'dayjs';
import { AssetMeta } from '/@/modules/asset/models/meta/AssetMeta';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { AssetType } from '/@/modules/asset/enums/AssetType';
import NumeralService from '/@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import { mix } from 'mixwith/mixwith';
import { AssetFormMixin } from '/@/modules/asset/models/mixins/AssetFormMixin';
import { BelongingType } from '/@/modules/asset/enums/BelongingType';
import { LifeInsuranceType } from '/@/modules/asset/enums/LifeInsuranceType';
import { LifeInsurancePlan } from '/@/modules/asset/enums/LifeInsurancePlan';
import { PrivateStockType } from '/@/modules/asset/enums/PrivateStockType';
import { PensionPlanType } from '/@/modules/asset/enums/PensionPlanType';
import { AssetStatus } from '/@/modules/asset/enums/AssetStatus';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { AssetShareOwnershipType } from '/@/modules/asset/enums/AssetShareOwnershipType';
import Lang from '/@/modules/core/app/services/Lang';
import { AccountingOwnershipFormMixin } from '/@/modules/accounting/components/mixins/AccountingOwnershipFormMixin';
import { ResidenceType } from '/@/modules/asset/enums/ResidenceType';
import { reduceJoin } from '/@/modules/core/app/helpers/StringHelper';
import { VehicleType } from '/@/modules/asset/enums/VehicleType';
import { RecreationalProductsType } from '/@/modules/asset/enums/RecreationalProductsType';
import { AssetLocation } from '/@/modules/asset/enums/AssetLocation';
import { FinancialAccountSubType } from '@/modules/financialaccount/constants/FinancialAccountSubType';
import { isNil } from 'lodash';

export class Asset extends mix(BaseEntityModel).with(AssetFormMixin, AccountingOwnershipFormMixin) {
  /**
   * @var {number}
   */
  id;

  /**
   * @var {string}
   */
  modelType;

  /**
   * @var {number}
   */
  modelId;

  /**
   * @var {number}
   */
  estateId;

  /**
   * @var {number}
   */
  assetId;

  /**
   * @var {?string}
   */
  name;

  /**
   * @var {string}
   */
  type;

  /**
   * @var {string}
   */
  status;

  /**
   * @var {boolean}
   */
  isCompleted;

  /**
   * @var {number}
   */
  displayValue;

  /**
   * @var {number}
   */
  currentValue;

  /**
   * @var {number}
   */
  valueToTheEstate;

  /**
   * @var {number}
   */
  atDeathValue;

  /**
   * @var {number}
   */
  estimatedValue;

  /**
   * @var {number}
   */
  appraisedValue;

  /**
   * @var {number}
   */
  liquidatedValue;

  /**
   * @var {number}
   */
  closingCost;

  /**
   * @var {number}
   */
  estateWorth;

  /**
   * @var {number}
   */
  purchasePrice;

  /**
   * @var {number}
   */
  currencyId;

  /**
   * @var {number}
   */
  jurisdictionId;

  /**
   * @var {number}
   */
  countryId;

  /**
   * @var {boolean}
   */
  isLocatedOutsideJurisdiction;

  /**
   * @var {number}
   */
  createdByUserId;

  /**
   * @var {boolean}
   */
  needsDocuments;

  /**
   * @var isLiquid
   */
  isLiquid;

  /**
   * @var {boolean}
   */
  isProbatable;

  /**
   * @var {boolean}
   */
  isProbateConfirmed;

  /**
   * @var {string}
   */
  dateAcquired;

  /**
   * @var {boolean}
   */
  isInherited;

  /**
   * @var {number}
   */
  valueAtMarriage;

  /**
   * @var {string}
   */
  usage;

  /**
   * @var {boolean}
   */
  isLocked;

  /**
   * @var {AssetMeta}
   */
  meta;

  /**
   * @relationship
   * @var {Currency}
   */
  currency;

  /**
   * @relationship
   * @var {DocumentCollection|Document[]}
   */
  documents;

  /**
   * @relationship
   * @var {Contact[]}
   */
  contacts;

  /**
   * @relationship
   * @var {EstateAccount[]}
   */
  estateAccounts;

  /**
   * @relationship
   * @var {FinancialAccount}
   */
  financialAccount;

  /**
   * @relationship
   * @var {FinancialAccountTransaction[]}
   */
  financialAccountTransactions;

  /**
   * @relationship
   * @var {Address}
   */
  address;

  /**
   * @relationship
   * @var {Note[]}
   */
  notes;

  /**
   * @relationship
   * @var {Audit[]}
   */
  audits;

  /**
   * @relationship
   * @var {EstateAccountingQuebecDistribution}
   */
  estateAccountingQuebecDistribution;

  /**
   * @relationship
   * @var {asset}
   */
  asset;

  /**
   * @var {AssetEstateFormCollection}
   */
  estateForms;

  /**
   * @var {StockHoldings[]}
   */
  stockHoldings;

  /**
   * @var {LiabilityCollection}
   */
  linkedLiabilities;

  /**
   * @var {EstateDistribution[]}
   */
  estateDistributions;

  /**
   * @relationship
   * @var {ReferralPrefillingInventory}
   */
  referralPrefillingInventory;

  /**
   * @relationship
   * @var {ReferralPrefillingInventory}
   */
  partnerReferralPrefillingInventory;

  constructor(model = {}) {
    super(model);
    super.setRelationships(this, model, this.getModelName());

    this.id = model.id;
    this.estateId = model.estateId;
    this.assetId = model.assetId;
    this.modelType = model.modelType;
    this.modelId = model.modelId;
    this.name = model.name;
    this.type = model.type;
    this.status = model.status;
    this.isCompleted = model.isCompleted;
    this.documents = model.documents;
    this.displayValue = model.displayValue;
    this.currentValue = model.currentValue;
    this.valueToTheEstate = model.valueToTheEstate;
    this.atDeathValue = model.atDeathValue;
    this.appraisedValue = model.appraisedValue;
    this.estimatedValue = model.estimatedValue;
    this.liquidatedValue = model.liquidatedValue;
    this.closingCost = model.closingCost;
    this.purchasePrice = model.purchasePrice;
    this.currencyId = model.currencyId || null;
    this.meta = AssetMeta.factory(this.type, model.meta);
    this.jurisdictionId = model.jurisdictionId;
    this.countryId = model.countryId;
    this.isLocatedOutsideJurisdiction = model.isLocatedOutsideJurisdiction;
    this.createdByUserId = model.createdByUserId;
    this.needsDocuments = model.needsDocuments || false;
    this.isLiquid = model.isLiquid ?? false;
    this.isProbatable = model.isProbatable;
    this.estateWorth = model.estateWorth;
    this.isProbateConfirmed = model.isProbateConfirmed;
    this.dateAcquired = model.dateAcquired;
    this.isInherited = model.isInherited;
    this.valueAtMarriage = model.valueAtMarriage;
    this.usage = model.usage;
    this.isLocked = model.isLocked;
  }

  /**
   *
   * @returns {string}
   */
  getModelName() {
    return ModelType.ASSET;
  }

  /**
   * @returns {number}
   */
  get value() {
    return this.appraisedValue || this.currentValue || this.displayValue;
  }

  /**
   * @returns {boolean}
   */
  get hasShareOfOwnershipType() {
    return !!this.meta.shareOwnershipType;
  }

  /**
   * @returns {boolean}
   */
  get hasLocation() {
    return !!this.meta.location;
  }

  /**
   * @returns {boolean}
   */
  get hasAddress() {
    return !!this.address?.id;
  }

  get isArtOrJewelry() {
    return this.type === AssetType.ART_JEWELRY.key;
  }

  get isBelonging() {
    return this.type === AssetType.BELONGING.key;
  }

  get isCash() {
    return this.type === AssetType.CASH.key;
  }

  get isFireArm() {
    return this.type === AssetType.FIREARM.key;
  }

  get isCryptoCurrency() {
    return this.type === AssetType.CRYPTOCURRENCY.key;
  }

  get isPrivateStock() {
    return this.type === AssetType.PRIVATE_STOCK.key;
  }

  /**
   * @returns {boolean}
   */
  get isPensionPlan() {
    return this.type === AssetType.PENSION_PLAN.key;
  }

  /**
   * @returns {boolean}
   */
  get isIncome() {
    return this.type === AssetType.INCOME.key;
  }

  /**
   * @returns {boolean}
   */
  get isFuneralPreArrangement() {
    return this.type === AssetType.FUNERAL_PRE_ARRANGEMENT.key;
  }

  /**
   * @returns {boolean}
   */
  get isLifeInsurance() {
    return this.type === AssetType.LIFE_INSURANCE.key;
  }

  /**
   * @returns {boolean}
   */
  get isRealEstate() {
    return this.type === AssetType.REAL_ESTATE.key;
  }

  /**
   * @returns {boolean}
   */
  get isBankAccount() {
    return this.type === AssetType.BANK_ACCOUNT.key;
  }

  /**
   * @returns {boolean}
   */
  get isVehicle() {
    return this.type === AssetType.VEHICLE.key;
  }

  /**
   * @returns {string}
   */
  get belongingTypeValue() {
    return BelongingType.getByKey(this.meta.type).value;
  }

  /**
   * @returns {string}
   */
  get lifeInsuranceTypeValue() {
    return this.meta.type ? LifeInsuranceType.getByKey(this.meta.type).value : '';
  }

  /**
   * @returns {string}
   */
  get lifeInsurancePlanValue() {
    return this.meta.plan ? LifeInsurancePlan.getByKey(this.meta.plan).value : '';
  }

  /**
   * @returns {string}
   */
  get stockTypeValue() {
    return PrivateStockType.getByKey(this.meta.type).value;
  }

  /**
   * @returns {string}
   */
  get pensionPlanTypeValue() {
    return this.meta.type ? PensionPlanType.getByKey(this.meta.type).value : '';
  }

  /**
   * @returns {string}
   */
  get realEstateTypeValue() {
    return ResidenceType.getByKey(this.meta.residenceType).value;
  }

  get isPrimaryResidence() {
    return this.isRealEstate && this.meta.residenceType === ResidenceType.PRIMARY.key;
  }

  /**
   * @returns {string}
   */
  get formattedDisplayValue() {
    return NumeralService.formatValue(
      this.displayValue,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      ''
    );
  }

  /**
   * @returns {string}
   */
  get formattedDisplayValueWithCurrency() {
    return NumeralService.formatValue(
      this.displayValue,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedLiquidationValue() {
    return NumeralService.formatValue(
      this.liquidatedValue,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      ''
    );
  }

  /**
   * @returns {string}
   */
  get formattedPurchaseValue() {
    return NumeralService.formatValue(
      this.purchasePrice,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      ''
    );
  }

  /**
   * @returns {string}
   */
  get formattedAtDeathValue() {
    return NumeralService.formatValue(
      this.atDeathValue,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      ''
    );
  }

  /**
   * @returns {string}
   */
  get formattedValueToTheEstateWithCurrency() {
    return NumeralService.formatValue(
      this.valueToTheEstate ?? 0,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedPurchasePriceWithCurrency() {
    return NumeralService.formatValue(
      this.purchasePrice,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedAtDeathValueWithCurrency() {
    return NumeralService.formatValue(
      this.atDeathValue,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedCurrentValueWithCurrency() {
    return NumeralService.formatValue(
      this.currentValue,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedLiquidatedValueWithCurrency() {
    return NumeralService.formatValue(
      this.liquidatedValue,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  get priceSold() {
    return this.liquidatedValue + this.closingCost;
  }

  set priceSold(value) {
    this.liquidatedValue = value - (this.closingCost ?? 0);
  }

  /**
   * @returns {string}
   */
  get formattedPriceSoldWithCurrency() {
    return NumeralService.formatValue(this.priceSold, ColumnFormat.FORMAT_CURRENCY, 2, false, '$');
  }

  /**
   * @returns {string}
   */
  get formattedLinkedLiabilitesValueWithCurrency() {
    if (this.isLiquidated) {
      return NumeralService.formatValue(
        this.linkedLiabilities.totalAmountPaid,
        ColumnFormat.FORMAT_CURRENCY,
        2,
        false,
        '$'
      );
    }

    return NumeralService.formatValue(
      this.linkedLiabilities.totalValueToTheEstate,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedCapitalGainsDuringLifeWithCurrency() {
    return NumeralService.formatValue(
      this.capitalGainsDuringLife,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedCapitalGainsAfterDeathWithCurrency() {
    return NumeralService.formatValue(
      this.capitalGainsAfterDeath,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedTotalCapitalGainsWithCurrency() {
    return NumeralService.formatValue(
      this.totalCapitalGains,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get updatedAtFromNow() {
    return dayjs(this.updatedAt).fromNow();
  }

  /**
   * @returns {string}
   */
  get estateSharePercentage() {
    return NumeralService.formatValue(
      this.meta.sharePercentage ?? 100,
      ColumnFormat.FORMAT_PERCENT,
      2
    );
  }

  /**
   * @returns {AssetType}
   */
  get typeEnum() {
    return AssetType.getByKey(this.type);
  }

  /**
   * @returns {string}
   */
  get displayType() {
    return AssetType.getByKey(this.type).value;
  }

  /**
   * @returns {string}
   */
  get displayTypeColor() {
    return AssetType.getByKey(this.type).color;
  }

  /**
   * @returns {AssetStatus}
   */
  get statusEnum() {
    return AssetStatus.getByKey(this.status);
  }

  /**
   * @returns {string}
   */
  get displayModelName() {
    return Lang.t('accounting.asset-lowercase');
  }

  /**
   * @returns {string}
   */
  get displayModelNameWithDefiniteArticle() {
    return Lang.t('accounting.the-asset-lowercase');
  }

  /**
   * @returns {boolean}
   */
  get isLiquidated() {
    return this.status === AssetStatus.LIQUIDATED.key;
  }

  /**
   * @returns {boolean}
   */
  get isReceived() {
    return this.status === AssetStatus.RECEIVED.key;
  }

  /**
   * @returns {boolean}
   */
  get isDiscarded() {
    return this.status === AssetStatus.DISCARDED.key;
  }

  /**
   * @returns {boolean}
   */
  get isDistributed() {
    return this.status === AssetStatus.DISTRIBUTED.key;
  }

  /**
   * @returns {string}
   */
  get displayOwnershipType() {
    return this.hasShareOfOwnershipType
      ? `${AssetShareOwnershipType.getByKey(this.meta.shareOwnershipType).value} ${
          this.meta.shareOwnershipType === AssetShareOwnershipType.SOLE.key
            ? ''
            : Lang.t('asset.panel-ownership')
        }`
      : '';
  }

  /**
   * @returns {AssetShareOwnershipType|undefined}
   */
  get shareOwnershipType() {
    return AssetShareOwnershipType.getByKey(this.meta?.shareOwnershipType);
  }

  /**
   * @returns {string}
   */
  get displayLocation() {
    return this.hasLocation ? `${AssetLocation.getByKey(this.meta.location).value}` : '';
  }

  /**
   * @returns {boolean}
   */
  get isNameEditable() {
    return [
      AssetType.ART_JEWELRY.key,
      AssetType.BELONGING.key,
      AssetType.REAL_ESTATE.key,
      AssetType.LAND.key,
      AssetType.PENSION_PLAN.key,
      AssetType.LIFE_INSURANCE.key
    ].includes(this.type);
  }

  /**
   * @returns {string}
   */
  get title() {
    if (this.name && this.isNameEditable) {
      return this.name;
    }

    switch (this.type) {
      case AssetType.ART_JEWELRY.key:
        return this.formatName(this.name, this.meta.type);
      case AssetType.BANK_ACCOUNT.key:
        return this.formatName(
          this.financialAccount?.name,
          this.financialAccount?.subType
            ? FinancialAccountSubType.getByKey(this.financialAccount.subType).value
            : ''
        );
      case AssetType.CRYPTOCURRENCY.key:
        return this.formatName(this.meta.name, this.meta.exchange);
      case AssetType.FIREARM.key:
        return this.formatName(this.meta.make, this.meta.model);
      case AssetType.LIFE_INSURANCE.key:
        return this.formatName(AssetType.LIFE_INSURANCE.value, this.lifeInsurancePlanValue);
      case AssetType.PENSION_PLAN.key:
        return this.formatName(
          this.meta.pensionPlanProvider.name,
          this.meta.type ? this.pensionPlanTypeValue : ''
        );
      case AssetType.PRIVATE_STOCK.key:
        return this.formatName(
          this.meta.issuedBy,
          this.meta.purposeValue,
          this.meta.legalStructureValue
        );
      case AssetType.REAL_ESTATE.key:
        return this.formatName(this.realEstateTypeValue, this.address.shortAddress);
      case AssetType.VEHICLE.key:
        if (this.meta.category === VehicleType.PASSENGER_CARS.key) {
          return this.formatName(this.meta.vehicleMake?.name, this.meta.vehicleModel?.name);
        }
        if (this.meta.category === VehicleType.RECREATIONAL_PRODUCTS.key) {
          return this.formatName(
            RecreationalProductsType.getByKey(this.meta.recreationalProductType)?.value,
            this.meta.recreationalProductModel,
            this.meta.year
          );
        }
        return this.meta.title;
      case AssetType.OTHER.key:
        return this.formatName(this.name, this.meta.type);
      case AssetType.LAND.key:
        return this.formatName(
          this.meta.types?.map((type) => type.value).join(', '),
          this.meta.propertyIdentificationNumber
        );
      default:
        return this.meta.title;
    }
  }

  /**
   * @returns {boolean}
   */
  get hasEstateAccount() {
    return this.estateAccounts?.length > 0;
  }

  /**
   * @returns {boolean}
   */
  get hasAsset() {
    return !!this.asset && !!this.asset.id;
  }

  /**
   * @returns {boolean}
   */
  get hasAssetWithOwnership() {
    if (this.hasAsset) {
      return this.asset?.meta.hasPercentShare;
    }
    return false;
  }

  get hasReferralPrefillingInventory() {
    return !!this.referralPrefillingInventory || !!this.partnerReferralPrefillingInventory;
  }

  /**
   * @param args
   * @returns {string}
   */
  formatName(...args) {
    return reduceJoin(args, ' - ');
  }

  get formattedName() {
    if (AssetType.BANK_ACCOUNT.key === this.type && this.financialAccount?.number) {
      return `${this.financialAccount.name} - ${this.financialAccount.bankAccountSubType} (${this.financialAccount.maskedAccountNumberLastFourDigits})`;
    }

    if (this.type === AssetType.LIFE_INSURANCE.key) {
      return `${this.name} - ${Lang.t('asset.life-insurance-plan')}`;
    }

    return this.name;
  }

  /**
   * @returns {boolean}
   */
  get canAssignNamedBeneficiaries() {
    if (AssetType.canAssignNamedBeneficiaries(this.type)) {
      if (!this.isBankAccount) {
        return true;
      }

      if (!this.financialAccount?.isRegisteredInvestmentSubType) {
        return false;
      }

      return true;
    }

    return false;
  }

  /**
   * @returns {User[]}
   * For Cashflows V2
   */
  get namedBeneficiaryUsers() {
    if (!this.canAssignNamedBeneficiaries) {
      return [];
    }

    return this.estateAccounts?.map((account) => account.user).filter((user) => !!user) ?? [];
  }

  /**
   * @returns {User[]}
   * For Cashflows V2
   */
  get coOwnerUsers() {
    if (this.canAssignNamedBeneficiaries) {
      return [];
    }

    return this.estateAccounts?.map((account) => account.user).filter((user) => !!user) ?? [];
  }

  /**
   * @returns {User[]}
   * For Cashflows V2
   */
  get specificBeneficiaryUsers() {
    return (
      this.estateDistributions
        ?.map((distribution) => distribution.estateAccount?.user)
        .filter((user) => !!user) ?? []
    );
  }

  /**
   *  @returns {boolean}
   */
  get canHaveCapitalGains() {
    if (isNil(this.capitalGainsDuringLife) && isNil(this.capitalGainsAfterDeath)) {
      return false;
    }

    return this.totalCapitalGains > 0;
  }

  /**
   * @returns {number}
   */
  get capitalGainsDuringLife() {
    if (isNil(this.atDeathValue) || isNil(this.purchasePrice)) {
      return null;
    }

    return this.atDeathValue - this.purchasePrice;
  }

  /**
   * @returns {number|null}
   */
  get capitalGainsAfterDeath() {
    if (isNil(this.liquidatedValue) || isNil(this.atDeathValue)) {
      return null;
    }

    return this.liquidatedValue - this.atDeathValue;
  }

  /**
   * @returns {number}
   */
  get totalCapitalGains() {
    return (this.capitalGainsDuringLife ?? 0) + (this.capitalGainsAfterDeath ?? 0);
  }
}
